import React, { Fragment, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import { TRUNCATE_CELL_CLASS } from './ActualCostsAllocationSubRow';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { formatFigure } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TableFilterModalThreeHandlers from 'CommonComponents/TableFilterModalThreeHandlers';
import TablePagination from 'CommonComponents/TablePagination';
import TableSearchModal from 'CommonComponents/TableSearchModal';
import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

import useSort, { SORT_DIR_ASC } from 'Hooks/useSort';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const EMPTY_CELL = '—';

const EMPTY_KEY = 'Empty';

const COLUMN_COUNT = 17;

/**
 * Sort keys, and how to read the value each one sorts on off a row.
 *
 * Sorting is applied WITHIN a month group, not across the page: the payload
 * arrives month-grouped and paginated, so a page-wide sort would silently
 * reorder rows out from under their own month heading.
 */
const SORT_ACCESSORS = {
    description: (row) => row.transaction_description,
    supplier: (row) => row.supplier || row.supplier_name,
    documentType: (row) => row.document_type,
    documentNumber: (row) => row.document_number,
    date: (row) => row.date,
    glCode: (row) => row.gl_code,
    amount: (row) => Number(row.amount ?? 0),
    archivedBy: (row) => row.archived_by_name,
    archivedAt: (row) => row.archived_at,
    reason: (row) => row.archive_reason,
};

function compareRows(a, b, accessor, dir) {
    const left = accessor(a);
    const right = accessor(b);

    // Blanks always sink, whichever way the column is pointing — a column of
    // dashes floating to the top is never what the reconciler asked for.
    if (left === null || left === undefined || left === '') {
        return right === null || right === undefined || right === '' ? 0 : 1;
    }
    if (right === null || right === undefined || right === '') {
        return -1;
    }

    const result = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right));

    return dir === SORT_DIR_ASC ? result : -result;
}

/**
 * Read-only listing of the archived transactions for one actual-cost type.
 *
 * Deliberately NOT ActualCostsAllocationTable. That table's sub-row clears an
 * allocation whose budget line is missing from the freshly rebuilt option list —
 * which on an archived row would destroy the very allocations Restore exists to
 * bring back. Restore is the only action here; everything else is display.
 */
export default function ArchivedActualCostsTable(props) {
    const [collapsedMonths, setCollapsedMonths] = useState({});
    const [reasonQuery, setReasonQuery] = useState(props.filters?.search?.reason ?? '');
    const [supplierQuery, setSupplierQuery] = useState(props.filters?.search?.supplier ?? '');
    const [documentTypeQuery, setDocumentTypeQuery] = useState(props.filters?.search?.documentType ?? '');
    const [glCodeQuery, setGlCodeQuery] = useState(props.filters?.search?.glCode ?? '');

    // Checkbox option lists for the three simple header filters, built from the
    // ARCHIVED option lists the filters endpoint serves (`tableFilters.archived`).
    // The live tabs' lists would omit a value whose only transaction has been
    // archived — exactly the value this tab needs to offer.
    const [suppliers, setSuppliers] = useState({});
    const [documentTypes, setDocumentTypes] = useState({});
    const [glCodes, setGlCodes] = useState({});

    const sort = useSort();

    const glCodeLabel = (code) => {
        const glCode = code === EMPTY_KEY ? null : props.glCodes?.[code];

        return glCode ? glCode.code + ' - ' + glCode.name : code;
    };

    // Rebuild each dict from the server's option list, preserving any tick the
    // reconciler has already made and dropping options that no longer exist.
    useEffect(() => {
        const rebuild = (names, previous, toKey = (value) => value) => {
            const next = {};
            (names ?? []).forEach((name) => {
                const key = toKey(name || EMPTY_KEY);
                next[key] = key in previous ? previous[key] : true;
            });
            return next;
        };

        setSuppliers((previous) => rebuild(props.tableFilters?.suppliers, previous));
        setDocumentTypes((previous) => rebuild(props.tableFilters?.documentTypes, previous));
        setGlCodes((previous) => rebuild(props.tableFilters?.glCodes, previous, glCodeLabel));
    }, [props.tableFilters, props.glCodes]);

    const setAll = (setter, dict, value) => {
        const next = {};
        Object.keys(dict).forEach((key) => {
            next[key] = value;
        });
        setter(next);
    };

    /**
     * Clear every filter and search box on the page's word — the counterpart of
     * clearColumnFiltersSignal on ActualCostsAllocationTable. The rebuild effect
     * above deliberately preserves existing ticks, so the page resetting its own
     * payload is not enough to put these back to "everything shown".
     */
    useEffect(() => {
        if (!props.clearColumnFiltersSignal) {
            return;
        }

        setSuppliers((previous) => Object.fromEntries(Object.keys(previous).map((key) => [key, true])));
        setDocumentTypes((previous) => Object.fromEntries(Object.keys(previous).map((key) => [key, true])));
        setGlCodes((previous) => Object.fromEntries(Object.keys(previous).map((key) => [key, true])));

        setReasonQuery('');
        setSupplierQuery('');
        setDocumentTypeQuery('');
        setGlCodeQuery('');
    }, [props.clearColumnFiltersSignal]);

    const toggleKey = (setter, key) => setter((previous) => ({ ...previous, [key]: !previous[key] }));

    // Every option ticked means "no filter" — matches isFilterActive() on the
    // sibling table, so the header icon only lights up when something is hidden.
    const isFilterActive = (dict) => Object.values(dict).some((shown) => !shown);

    // Typed id → display name, flattened once per payload. The archived table
    // only ever reads these; the editable table's grouped option builders exist
    // to populate selects it does not have.
    const departmentNames = useMemo(() => {
        const names = {};
        [...(props.departments?.expense ?? []), ...(props.departments?.revenue ?? [])].forEach((department) => {
            names[department.typed_id] = department.name;
        });
        return names;
    }, [props.departments]);

    const budgetLineNames = useMemo(() => {
        const names = {};
        Object.values(props.directLineItems ?? {}).forEach((groups) => {
            Object.values(groups ?? {}).forEach((lines) => {
                (lines ?? []).forEach((line) => {
                    const label = line.group_label ? `${line.group_label} / ${line.name}` : line.name;
                    // Matches the live table's ActualCostsAllocationTable.js
                    // budgetLineFilterName(): a legacy template/pack child reads
                    // identically to a normal line without this.
                    names[line.typed_id] = line.is_legacy_child ? $t('{{name}} (legacy)', { name: label }) : label;
                });
            });
        });
        return names;
    }, [props.directLineItems]);

    const contractReferences = useMemo(() => {
        const references = {};
        Object.values(props.allContracts ?? {}).forEach((byType) => {
            Object.values(byType ?? {}).forEach((contracts) => {
                (contracts ?? []).forEach((contract) => {
                    references[contract.typed_id] = contract.reference;
                });
            });
        });
        return references;
    }, [props.allContracts]);

    const formatMonth = (month) => {
        const locale = typeof window.CURRENT_LOCALE === 'string' ? window.CURRENT_LOCALE.replace('_', '-') : undefined;

        return new Date(month + '-01').toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    };

    const toggleMonth = (month) => {
        setCollapsedMonths((previous) => ({ ...previous, [month]: previous[month] === false }));
    };

    const isMonthOpen = (month) => collapsedMonths[month] !== false;

    const sortRows = (rows) => {
        const accessor = SORT_ACCESSORS[sort.type];

        if (!accessor) {
            return rows;
        }

        return [...rows].sort((a, b) => compareRows(a, b, accessor, sort.dir));
    };

    const sortAriaValue = (key) => {
        if (sort.isAsc(key)) {
            return 'ascending';
        }
        return sort.isDesc(key) ? 'descending' : 'none';
    };

    const sortableHeader = (key, label, className) => (
        <th className={classNames(className, 'tw-cursor-pointer')} onClick={() => sort.toggleSort(key)} aria-sort={sortAriaValue(key)}>
            <TableSortHeaderCell asc={sort.isAsc(key)} desc={sort.isDesc(key)}>
                {label}
            </TableSortHeaderCell>
        </th>
    );

    /**
     * A sortable header that also carries the sibling tables' checkbox filter and
     * free-text search controls.
     *
     * The sort toggle is a button rather than a click handler on the <th>, so
     * opening a filter popover does not also re-sort the column underneath it.
     */
    const filterableHeader = ({ sortKey, label, className, filterKey, searchKey, dict, setDict, headerText, searchHeaderText, placeholder }) => (
        <th className={className}>
            <div className="tw-flex tw-items-baseline tw-justify-center">
                <button type="button" className="tw-bg-transparent tw-p-0" onClick={() => sort.toggleSort(sortKey)} aria-sort={sortAriaValue(sortKey)}>
                    <TableSortHeaderCell asc={sort.isAsc(sortKey)} desc={sort.isDesc(sortKey)}>
                        {label}
                    </TableSortHeaderCell>
                </button>
                <div className="tw-ms-2">
                    <TableFilterModalThreeHandlers
                        id={`${props.storeKey}_${filterKey}_filter-modal`}
                        headerText={headerText}
                        customIconStyle={'small'}
                        onApply={() => props.setFilters(filterKey, dict, true)}
                        applyText={$t('Apply')}
                        onProcess={() => setAll(setDict, dict, true)}
                        processText={$t('Show all')}
                        showProcess={true}
                        onCancel={() => setAll(setDict, dict, false)}
                        showCancel={true}
                        cancelText={$t('Hide all')}
                        closeOnAction={false}
                        contentMaxHeight={'30vh'}
                        isActive={isFilterActive(dict)}
                    >
                        <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                            {Object.keys(dict).map((key) => (
                                <div key={key}>
                                    <CheckboxInput label={key} checked={!!dict[key]} onChange={() => toggleKey(setDict, key)} />
                                </div>
                            ))}
                        </div>
                    </TableFilterModalThreeHandlers>
                </div>
                <div className="tw-ps-2">
                    <TableSearchModal
                        id={`${props.storeKey}_${filterKey}_search-modal`}
                        headerText={searchHeaderText}
                        placeholder={placeholder}
                        value={searchKey.value}
                        onChange={searchKey.set}
                        onClear={() => {
                            searchKey.set('');
                            props.setFilters(searchKey.name, '', false);
                        }}
                        closeOnAction={false}
                        customIconStyle="small"
                        isActive={!!props.filters?.search?.[searchKey.name]}
                        onApply={() => props.setFilters(searchKey.name, searchKey.value, false)}
                    />
                </div>
            </div>
        </th>
    );

    const months = Object.keys(props.data ?? {});

    return (
        <div className="tw-mb-3">
            <div className="tw-flex tw-items-start tw-justify-between">
                <h3 className="tw-my-3">{props.header}</h3>
                <button className="table__icon-btn tw-text-white" onClick={props.toggleCollapsed}>
                    {!props.collapsed && <PlusIcon className="tw-h-3.5 tw-w-3.5" />}
                    {props.collapsed && <MinusIcon className="tw-h-3.5 tw-w-3.5" />}
                </button>
            </div>

            <div className="table__pagination-info">
                <PaginationInfo to={props.pagination.to} from={props.pagination.from} total={props.pagination.total} />
                <TablePagination
                    lastPage={props.pagination.lastPage}
                    showPerPage={true}
                    perPage={props.pagination.perPage}
                    currentPage={props.pagination.page}
                    perPageOptions={[10, 20, 40, 50, 100, 200]}
                    onPerPageChange={(value) => props.setPaginationPerPage(value)}
                    pageChange={(data) => props.setPaginationPage(+data.selected + 1)}
                />
            </div>

            <div className={classNames('inbud-table_wrapper', 'tw-overflow-auto', 'max-h-50vh', 'tw-relative', !props.collapsed && '!tw-hidden')}>
                <table className="reconciliation-table inbud-table inbud-table_first-two-sticky-80" style={{ width: 'max-content' }}>
                    <thead className="tw-sticky tw-top-0" style={{ zIndex: 15 }}>
                        <tr>
                            {sortableHeader('description', $t('Description'), 'tw-w-80')}
                            {filterableHeader({
                                sortKey: 'supplier',
                                label: $t('Supplier'),
                                className: 'tw-w-80',
                                filterKey: 'supplier',
                                searchKey: { name: 'supplier', value: supplierQuery, set: setSupplierQuery },
                                dict: suppliers,
                                setDict: setSuppliers,
                                headerText: $t('Displayed Suppliers'),
                                searchHeaderText: $t('Search Suppliers'),
                                placeholder: $t('Type supplier name…'),
                            })}
                            {filterableHeader({
                                sortKey: 'documentType',
                                label: $t('Document Type'),
                                className: 'tw-w-44',
                                filterKey: 'documentType',
                                searchKey: { name: 'documentType', value: documentTypeQuery, set: setDocumentTypeQuery },
                                dict: documentTypes,
                                setDict: setDocumentTypes,
                                headerText: $t('Displayed Document Types'),
                                searchHeaderText: $t('Search Document Type'),
                                placeholder: $t('Type Document Type…'),
                            })}
                            {sortableHeader('documentNumber', $t('Document Reference'), 'tw-w-44')}
                            {sortableHeader('date', $t('Transaction Date'), 'tw-w-24')}
                            {filterableHeader({
                                sortKey: 'glCode',
                                label: $t('GL Code'),
                                className: 'tw-w-44',
                                filterKey: 'glCode',
                                searchKey: { name: 'glCode', value: glCodeQuery, set: setGlCodeQuery },
                                dict: glCodes,
                                setDict: setGlCodes,
                                headerText: $t('Displayed GL Codes'),
                                searchHeaderText: $t('Search GL Code'),
                                placeholder: $t('Type GL code or name…'),
                            })}
                            {sortableHeader('amount', $t('Amount'), 'tw-w-44')}
                            <th className="tw-w-72">{$t('Department / Cost Centre')}</th>
                            <th className="tw-w-72">{$t('Budget Line')}</th>
                            <th className="tw-w-44">{$t('Contract Reference')}</th>
                            <th className="tw-w-44">{$t('Type')}</th>
                            <th className="tw-w-44">{$t('Transaction')}</th>
                            <th className="tw-w-44">{$t('Allocated Amount')}</th>
                            {sortableHeader('archivedBy', $t('Archived by'), 'tw-w-44')}
                            {sortableHeader('archivedAt', $t('Archived on'), 'tw-w-44')}
                            <th className="tw-w-72">
                                <div className="tw-flex tw-items-baseline tw-justify-center">
                                    <button type="button" className="tw-bg-transparent tw-p-0" onClick={() => sort.toggleSort('reason')}>
                                        <TableSortHeaderCell asc={sort.isAsc('reason')} desc={sort.isDesc('reason')}>
                                            {$t('Reason')}
                                        </TableSortHeaderCell>
                                    </button>
                                    <div className="tw-ps-2">
                                        <TableSearchModal
                                            id={`${props.storeKey}_reason_search-modal`}
                                            headerText={$t('Search Reasons')}
                                            placeholder={$t('Type part of a reason…')}
                                            value={reasonQuery}
                                            onChange={setReasonQuery}
                                            onClear={() => {
                                                setReasonQuery('');
                                                props.setFilters('reason', '', false);
                                            }}
                                            closeOnAction={false}
                                            customIconStyle="small"
                                            isActive={!!props.filters?.search?.reason}
                                            onApply={() => props.setFilters('reason', reasonQuery, false)}
                                        />
                                    </div>
                                </div>
                            </th>
                            <th className="tw-w-32">{$t('Actions')}</th>
                        </tr>
                    </thead>

                    {!!props.request && (
                        <tbody>
                            <tr>
                                <td colSpan={COLUMN_COUNT}>
                                    <LoadingSpinner />
                                </td>
                            </tr>
                        </tbody>
                    )}

                    {!props.request && (
                        <tbody>
                            {months.map((month) => (
                                <Fragment key={month}>
                                    <tr>
                                        <td className="tw-cursor-pointer" style={{ zIndex: 14 }} colSpan={2} onClick={() => toggleMonth(month)}>
                                            <div className="tw-flex tw-items-center">
                                                <div className="tw-pe-2 tw-font-bold">{formatMonth(month)}</div>
                                                <span className="tw-pe-3 tw-text-xs tw-font-normal tw-text-charcoal-75">
                                                    — {$t('{{count}} archived', { count: props.data[month].length })}
                                                </span>
                                                {isMonthOpen(month) ? (
                                                    <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                                ) : (
                                                    <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                                )}
                                            </div>
                                        </td>
                                        <td colSpan={COLUMN_COUNT - 2}></td>
                                    </tr>

                                    {isMonthOpen(month) &&
                                        sortRows(props.data[month]).map((row) => {
                                            // A row with no allocations still renders one line, with the
                                            // allocation columns blank — it is an unreconciled transaction
                                            // that was archived, not a broken row.
                                            const allocations = row.costAllocations?.length ? row.costAllocations : [null];
                                            const span = allocations.length;

                                            return allocations.map((allocation, index) => (
                                                <tr key={`${row.id}-${allocation?.id ?? 'none'}`}>
                                                    {index === 0 && (
                                                        <Fragment>
                                                            <td className={TRUNCATE_CELL_CLASS} rowSpan={span}>
                                                                {row.transaction_description || EMPTY_CELL}
                                                            </td>
                                                            <td className={TRUNCATE_CELL_CLASS} rowSpan={span}>
                                                                {row.supplier || row.supplier_name || EMPTY_CELL}
                                                            </td>
                                                            <td className={TRUNCATE_CELL_CLASS} rowSpan={span}>
                                                                {row.document_type || EMPTY_CELL}
                                                            </td>
                                                            <td className={TRUNCATE_CELL_CLASS} rowSpan={span}>
                                                                {row.document_number || EMPTY_CELL}
                                                            </td>
                                                            <td rowSpan={span}>{formatDateWithTz(row.date, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY')}</td>
                                                            <td className={TRUNCATE_CELL_CLASS} rowSpan={span}>
                                                                {row.gl_code || EMPTY_CELL}
                                                            </td>
                                                            <td className="inbud-numeric-cell" rowSpan={span}>
                                                                {formatFigure(row.amount)}
                                                            </td>
                                                        </Fragment>
                                                    )}

                                                    <td className={TRUNCATE_CELL_CLASS}>{departmentNames[allocation?.typed_allocatable_id] || EMPTY_CELL}</td>
                                                    <td className={TRUNCATE_CELL_CLASS}>{budgetLineNames[allocation?.typed_valuable_id] || EMPTY_CELL}</td>
                                                    <td className={TRUNCATE_CELL_CLASS}>
                                                        {contractReferences[allocation?.typed_contractable_id] || EMPTY_CELL}
                                                    </td>
                                                    <td className={TRUNCATE_CELL_CLASS}>{allocation?.type || EMPTY_CELL}</td>
                                                    <td className={TRUNCATE_CELL_CLASS}>
                                                        {allocation ? (props.transactionTypes?.[allocation.transaction_type] ?? EMPTY_CELL) : EMPTY_CELL}
                                                    </td>
                                                    <td className="inbud-numeric-cell">{formatFigure(allocation?.amount)}</td>

                                                    {index === 0 && (
                                                        <Fragment>
                                                            <td className={TRUNCATE_CELL_CLASS} rowSpan={span}>
                                                                {row.archived_by_name || EMPTY_CELL}
                                                            </td>
                                                            <td rowSpan={span}>
                                                                {formatDateWithTz(row.archived_at, 'YYYY-MM-DD HH:mm:ss', 'DD/MM/YYYY HH:mm') || EMPTY_CELL}
                                                            </td>
                                                            <td className={TRUNCATE_CELL_CLASS} rowSpan={span} title={row.archive_reason || undefined}>
                                                                {row.archive_reason || EMPTY_CELL}
                                                            </td>
                                                            <td rowSpan={span}>
                                                                <Button
                                                                    variant="secondary"
                                                                    small
                                                                    disabled={props.restoringIds?.has(row.id)}
                                                                    onClick={() => props.onRestore(row)}
                                                                >
                                                                    {props.restoringIds?.has(row.id) ? $t('Restoring…') : $t('Restore')}
                                                                </Button>
                                                            </td>
                                                        </Fragment>
                                                    )}
                                                </tr>
                                            ));
                                        })}
                                </Fragment>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );
}
